import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiCallWithHost } from '../../utils/apiClient'

// Async thunks
export const fetchPublishedWorkouts = createAsyncThunk(
  'publishedWorkouts/fetchPublishedWorkouts',
  async (
    {
      token,
      profileId,
      filters = {},
      page = 0,
      size = 20,
      sortBy = 'publishedAt',
      sortDir = 'desc',
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        sortBy,
        sortDir,
      })

      // Add optional filters
      if (filters.namePattern) params.append('namePattern', filters.namePattern)
      if (filters.visibility) params.append('visibility', filters.visibility)
      if (filters.tag) params.append('tag', filters.tag)
      if (filters.popularOnly) params.append('popularOnly', 'true')
      if (filters.recentOnly) params.append('recentOnly', 'true')
      if (filters.includeArchived) params.append('includeArchived', 'true')

      const response = await apiCallWithHost(
        `/v2/workouts?${params.toString()}`,
        {
          method: 'GET',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const error = await response.text()
        return rejectWithValue(error || 'Failed to fetch published workouts')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch published workouts')
    }
  },
)

export const fetchPublishedWorkout = createAsyncThunk(
  'publishedWorkouts/fetchPublishedWorkout',
  async ({ token, profileId, id }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/workouts/${id}`,
        {
          method: 'GET',
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const error = await response.text()
        return rejectWithValue(error || 'Failed to fetch published workout')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch published workout')
    }
  },
)

export const archiveWorkout = createAsyncThunk(
  'publishedWorkouts/archiveWorkout',
  async ({ token, profileId, id, workout }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/workouts/${id}?forceArchive=true`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...workout, archived: true }),
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const error = await response.text()
        return rejectWithValue(error || 'Failed to archive workout')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to archive workout')
    }
  },
)

export const unarchiveWorkout = createAsyncThunk(
  'publishedWorkouts/unarchiveWorkout',
  async ({ token, profileId, id, workout }, { rejectWithValue }) => {
    try {
      const response = await apiCallWithHost(
        `/v2/workouts/${id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...workout, archived: false }),
        },
        token,
        profileId,
      )

      if (!response.ok) {
        const error = await response.text()
        return rejectWithValue(error || 'Failed to unarchive workout')
      }

      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to unarchive workout')
    }
  },
)

// Initial state
const initialState = {
  workouts: [],
  currentWorkout: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  pagination: {
    pageNumber: 0,
    pageSize: 20,
    totalPages: 0,
    totalElements: 0,
  },
  filters: {
    namePattern: '',
    visibility: '',
    tag: '',
    popularOnly: false,
    recentOnly: false,
    includeArchived: false,
  },
  sorting: {
    sortBy: 'publishedAt',
    sortDir: 'desc',
  },
}

// Slice
const publishedWorkoutsSlice = createSlice({
  name: 'publishedWorkouts',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    setPage: (state, action) => {
      state.pagination.pageNumber = action.payload
    },
    setSorting: (state, action) => {
      state.sorting = { ...state.sorting, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
    clearCurrentWorkout: (state) => {
      state.currentWorkout = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch published workouts
      .addCase(fetchPublishedWorkouts.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchPublishedWorkouts.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.workouts = action.payload.content || []
        state.pagination = {
          pageNumber: action.payload.number || 0,
          pageSize: action.payload.size || 20,
          totalPages: action.payload.totalPages || 0,
          totalElements: action.payload.totalElements || 0,
        }
      })
      .addCase(fetchPublishedWorkouts.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'Failed to fetch published workouts'
      })
      // Fetch single workout
      .addCase(fetchPublishedWorkout.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchPublishedWorkout.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.currentWorkout = action.payload
      })
      .addCase(fetchPublishedWorkout.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'Failed to fetch published workout'
      })
      // Archive workout
      .addCase(archiveWorkout.fulfilled, (state, action) => {
        // Update the workout in the list
        const index = state.workouts.findIndex((w) => w.id === action.payload.id)
        if (index !== -1) {
          state.workouts[index] = action.payload
        }
        // Update current workout if it's the same
        if (state.currentWorkout?.id === action.payload.id) {
          state.currentWorkout = action.payload
        }
      })
      // Unarchive workout
      .addCase(unarchiveWorkout.fulfilled, (state, action) => {
        // Update the workout in the list
        const index = state.workouts.findIndex((w) => w.id === action.payload.id)
        if (index !== -1) {
          state.workouts[index] = action.payload
        }
        // Update current workout if it's the same
        if (state.currentWorkout?.id === action.payload.id) {
          state.currentWorkout = action.payload
        }
      })
  },
})

// Actions
export const { setFilters, clearFilters, setPage, setSorting, clearError, clearCurrentWorkout } =
  publishedWorkoutsSlice.actions

// Selectors
export const selectPublishedWorkouts = (state) => state.publishedWorkouts?.workouts || []
export const selectCurrentPublishedWorkout = (state) => state.publishedWorkouts?.currentWorkout
export const selectPublishedWorkoutsStatus = (state) => state.publishedWorkouts?.status || 'idle'
export const selectPublishedWorkoutsError = (state) => state.publishedWorkouts?.error
export const selectPublishedWorkoutsPagination = (state) =>
  state.publishedWorkouts?.pagination || initialState.pagination
export const selectPublishedWorkoutsFilters = (state) =>
  state.publishedWorkouts?.filters || initialState.filters
export const selectPublishedWorkoutsSorting = (state) =>
  state.publishedWorkouts?.sorting || initialState.sorting

export default publishedWorkoutsSlice.reducer
